/**
 * Hook da curva pré as-of (#218 backend; aba Juros & Inflação #220).
 *
 * Cache module-level CHAVEADO por dataRef (classe do bug #206: cache único +
 * hook sem o filtro congela a navegação — aqui cada as-of tem entrada própria).
 * `dataRef` é STRING YYYY-MM-DD sempre (nunca Date — timezone do picker).
 * Polling 10min visibilidade-aware (#75) — o dado muda 1×/dia (cron 07:40).
 *
 * `data === null` sem erro = 404 do endpoint (data não materializada) —
 * estado de domínio (`notFound`), não falha.
 */
import { useEffect, useState } from "react";
import { getCurvaPre } from "../api/client";
import type { CurvaPreResponse } from "../api/types";
import { startVisiblePolling } from "./visiblePolling";

const REFETCH_MS = 10 * 60 * 1000;

interface CacheEntry {
  expiresAt: number;
  promise: Promise<CurvaPreResponse | null>;
}
const cache = new Map<string, CacheEntry>();

export function invalidateCurvaPreCache(): void {
  cache.clear();
}

export interface UseCurvaPreState {
  data: CurvaPreResponse | null;
  isLoading: boolean;
  error: Error | null;
  /** true = API respondeu 404 (data_ref não materializada). */
  notFound: boolean;
}

export function useCurvaPre(dataRef?: string): UseCurvaPreState {
  const [state, setState] = useState<UseCurvaPreState>({
    data: null,
    isLoading: true,
    error: null,
    notFound: false,
  });

  useEffect(() => {
    let cancel = false;
    const key = dataRef ?? "latest";

    function load() {
      const now = Date.now();
      const hit = cache.get(key);
      if (!hit || hit.expiresAt < now) {
        cache.set(key, { expiresAt: now + REFETCH_MS, promise: getCurvaPre(dataRef) });
      }
      cache.get(key)!.promise
        .then((data) => {
          if (cancel) return;
          setState({ data, isLoading: false, error: null, notFound: data === null });
        })
        .catch((err: Error) => {
          if (cancel) return;
          cache.delete(key);
          setState({ data: null, isLoading: false, error: err, notFound: false });
        });
    }

    setState((s) => ({ ...s, isLoading: true }));
    load();
    const stop = startVisiblePolling(load, REFETCH_MS);
    return () => {
      cancel = true;
      stop();
    };
  }, [dataRef]);

  return state;
}
